<script setup lang="ts">
import type { EffectScope } from "vue";
import type { DebugApp, DebugConfigAdapter, DebugSection, DebugToolsContext } from "./types";
import VButton from "@verbleif/lib/src/components/Button/VButton.vue";
import DialogFooter from "@verbleif/lib/src/components/Dialog/DialogFooter.vue";
import DialogTitle from "@verbleif/lib/src/components/Dialog/DialogTitle.vue";
import VIcon from "@verbleif/lib/src/components/Icon/VIcon.vue";
import VTabItem from "@verbleif/lib/src/components/Tabs/TabItem/VTabItem.vue";
import VTabList from "@verbleif/lib/src/components/Tabs/TabList/VTabList.vue";
import VTabPanel from "@verbleif/lib/src/components/Tabs/TabPanel/VTabPanel.vue";
import VTabPanels from "@verbleif/lib/src/components/Tabs/TabPanels/VTabPanels.vue";
import VTabs from "@verbleif/lib/src/components/Tabs/VTabs.vue";
import VTag from "@verbleif/lib/src/components/Tag/VTag.vue";
import { tryUseDialogLayer } from "@verbleif/lib/src/composables/Dialog/useDialogLayer";
import { useMediaQuery } from "@vueuse/core";
import { computed, effectScope, onBeforeUnmount, provide, ref } from "vue";
import { useI18n } from "vue-i18n";
import { sortDebugSections } from "./defineDebugSection";
import { DEBUG_TOOLS_CONTEXT } from "./types";
import { useDebugEnvironment } from "./useDebugEnvironment";

const {
  app,
  sections,
  config,
  initialSection = undefined,
  admin = () => false,
} = defineProps<{
  app: DebugApp
  sections: DebugSection[]
  config: DebugConfigAdapter
  initialSection?: string
  /** Whether the user is an administrator; shows the Beheerder label. */
  admin?: () => boolean
}>();

const emit = defineEmits<{
  close: []
}>();

const { t } = useI18n();
const layer = tryUseDialogLayer();
const isMobile = useMediaQuery("(max-width: 767px)");

const orderedSections = computed(() => sortDebugSections(sections));
const showRail = computed(() => orderedSections.value.length > 1);

function indexOfSection(id: string | undefined): number {
  if (id === undefined) {
    return 0;
  }
  const index = orderedSections.value.findIndex(section => section.id === id);
  return index === -1 ? 0 : index;
}

const activeIndex = ref(indexOfSection(initialSection));
const activeSection = computed(() => orderedSections.value[activeIndex.value] ?? orderedSections.value[0]);

const environment = useDebugEnvironment(app, config);
const isAdmin = computed(() => admin());

const TONE_VARIANTS = {
  primary: "is-primary",
  secondary: "is-secondary",
  warning: "is-warning",
  danger: "is-danger",
  info: "is-info",
  gray: "is-gray",
} as const;

function close() {
  emit("close");
  void layer?.close();
}

// State a section shares between its body and its footer lives as long as the dialog, in a scope of its own.
const stateScope: EffectScope = effectScope(true);
const sectionStates = new Map<string, unknown>();

function sectionState<T>(key: string, factory: () => T): T {
  if (!sectionStates.has(key)) {
    const created = stateScope.run(factory);
    if (created === undefined) {
      throw new Error(`Debug section state ${key} could not be created.`);
    }
    sectionStates.set(key, created);
  }
  return sectionStates.get(key) as T;
}

onBeforeUnmount(() => {
  stateScope.stop();
  sectionStates.clear();
});

const context: DebugToolsContext = {
  app,
  config,
  goTo: (sectionId: string) => {
    activeIndex.value = indexOfSection(sectionId);
  },
  hasSection: (sectionId: string) => orderedSections.value.some(section => section.id === sectionId),
  close,
  sectionState,
};
provide(DEBUG_TOOLS_CONTEXT, context);
</script>

<template>
  <div class="flex h-full min-h-0 flex-col" data-testid="debug-tools-dialog">
    <div class="flex items-start gap-3 border-b border-gray-200 px-5 pt-5 pb-4 pr-16 dark:border-dark-600">
      <span
        class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400"
        aria-hidden="true"
      >
        <VIcon name="bug" class="text-lg" />
      </span>
      <div class="flex min-w-0 flex-1 flex-col gap-1.5">
        <DialogTitle bare compact :title="t('debug_tools.title')" :help="t('debug_tools.help')" />
        <div class="flex flex-wrap items-center gap-1.5" data-testid="debug-tools-labels">
          <VTag :text="t(`debug_tools.apps.${app}`)" variant="is-gray" icon-less :title="t('debug_tools.app_label')" />
          <VTag
            :text="environment.label.value"
            :variant="TONE_VARIANTS[environment.tone.value]"
            icon-less
            :title="t('debug_tools.environment_label')"
            data-testid="debug-tools-environment"
          />
          <VTag
            v-if="isAdmin"
            :text="t('debug_tools.admin')"
            variant="is-primary"
            icon="user-shield"
            data-testid="debug-tools-admin"
          />
        </div>
      </div>
    </div>

    <div class="flex min-h-0 flex-1">
      <VTabs
        v-if="showRail"
        v-model:model-value="activeIndex"
        :orientation="isMobile ? 'horizontal' : 'vertical'"
        :tile="!isMobile"
        :scrollable="isMobile"
        lazy
        data-testid="debug-tools-rail"
      >
        <VTabList :aria-label="t('debug_tools.sections_label')">
          <VTabItem
            v-for="(section, index) in orderedSections"
            :key="section.id"
            :title="t(section.label)"
            :icon="section.icon"
            :index="index"
            :data-testid="`debug-tools-tab-${section.id}`"
          />
        </VTabList>
        <VTabPanels>
          <VTabPanel v-for="(section, index) in orderedSections" :key="section.id" :index="index">
            <div class="h-full overflow-y-auto p-5">
              <component :is="section.component" />
            </div>
          </VTabPanel>
        </VTabPanels>
      </VTabs>
      <div v-else-if="activeSection" class="min-h-0 flex-1 overflow-y-auto p-5">
        <component :is="activeSection.component" />
      </div>
    </div>

    <component :is="activeSection.footer" v-if="activeSection && activeSection.footer !== 'close'" />
    <DialogFooter v-else>
      <span />
      <VButton variant="gray" rounded data-testid="debug-tools-close" @click="close">
        {{ t("base.close") }}
      </VButton>
    </DialogFooter>
  </div>
</template>
